<template>
  <div v-if="featuredPosts.length > 0" class="featured-section">
    <div class="section-header">
      <h2 class="section-title">{{ $t('home.featured') }}</h2>
      <div class="section-divider"></div>
    </div>
    
    <!-- Desktop View -->
    <div class="desktop-view">
      <div class="featured-container">
        <!-- Main Featured Post -->
        <div class="main-featured">
          <router-link :to="`/post/${featuredPosts[0].slug}/${featuredPosts[0].id}`" class="main-featured-link">
            <div class="main-image-wrapper">
              <el-image
                v-if="featuredPosts[0].featured_image_url"
                :src="featuredPosts[0].featured_image_url"
                fit="cover"
                class="main-featured-img"
                :lazy="true"
              />
              <ArtPlaceholder v-else :post="featuredPosts[0]" :show-category="false" />
              <div class="main-overlay">
                <div class="overlay-content">
                  <div class="category-badge" v-if="featuredPosts[0].category">
                    {{ featuredPosts[0].category.name }}
                  </div>
                  <h1 class="main-title">{{ decodeHtml(featuredPosts[0].title) }}</h1>
                  <p class="main-excerpt">{{ decodeHtml(featuredPosts[0].excerpt) }}</p>
                  <div class="main-meta">
                    <span class="meta-date">{{ timeAgo(featuredPosts[0].created_at) }}</span>
                    <span class="read-more-arrow">→</span>
                  </div>
                </div>
              </div>
            </div>
          </router-link>
        </div>
        
        <!-- Secondary Featured Posts -->
        <div class="secondary-featured">
          <div 
            v-for="(post, index) in featuredPosts.slice(1, 4)" 
            :key="index"
            class="secondary-card"
          >
            <router-link :to="`/post/${post.slug}/${post.id}`" class="secondary-link">
              <div class="secondary-content">
                <div class="secondary-category" v-if="post.category">
                  {{ post.category.name }}
                </div>
                <h3 class="secondary-title">{{ decodeHtml(post.title) }}</h3>
                <p class="secondary-excerpt">{{ decodeHtml(post.excerpt) }}</p>
                <div class="secondary-footer">
                  <span class="secondary-date">{{ timeAgo(post.created_at) }}</span>
                  <span class="read-arrow">→</span>
                </div>
              </div>
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile View -->
    <div class="mobile-view">
      <div class="mobile-featured">
        <router-link :to="`/post/${featuredPosts[0].slug}/${featuredPosts[0].id}`" class="mobile-main-link">
          <div class="mobile-image-wrapper">
            <el-image
              v-if="featuredPosts[0].featured_image_url"
              :src="featuredPosts[0].featured_image_url"
              fit="cover"
              class="mobile-main-img"
            />
            <ArtPlaceholder v-else :post="featuredPosts[0]" :show-category="false" />
            <div class="mobile-overlay">
              <div class="mobile-category" v-if="featuredPosts[0].category">
                {{ featuredPosts[0].category.name }}
              </div>
              <h2 class="mobile-title">{{ decodeHtml(featuredPosts[0].title) }}</h2>
              <p class="mobile-excerpt">{{ decodeHtml(featuredPosts[0].excerpt) }}</p>
              <p class="mobile-date">{{ timeAgo(featuredPosts[0].created_at) }}</p>
            </div>
          </div>
        </router-link>
      </div>
      
      <div class="mobile-secondary">
        <div 
          v-for="(post, index) in featuredPosts.slice(1)" 
          :key="index"
          class="mobile-card"
        >
          <router-link :to="`/post/${post.slug}/${post.id}`" class="mobile-link">
            <div class="mobile-card-content">
              <div class="mobile-card-category" v-if="post.category">
                {{ post.category.name }}
              </div>
              <h3 class="mobile-card-title">{{ decodeHtml(post.title) }}</h3>
              <p class="mobile-card-excerpt">{{ decodeHtml(post.excerpt) }}</p>
              <p class="mobile-card-date">{{ timeAgo(post.created_at) }}</p>
            </div>
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

  
<script>
import { timeAgo } from '@/utils/date'
import { decodeHtml } from '@/utils/string'; // Import decodeHtml utility
import ArtPlaceholder from './ArtPlaceholder.vue';

export default {
  name: 'FeaturedPosts',
  components: {
    ArtPlaceholder,
  },
  props: {
    featuredPosts: {
      type: Array,
      required: true,
    },
  },
  setup() {
    console.log('FeaturedPostsComponent setup called');
    
    const isMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      console.log('User Agent:', userAgent);
      
      // Check for mobile devices
      const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
      const isMobileDevice = mobileRegex.test(userAgent.toLowerCase());
      
      // Check for touch support
      const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      
      // Check screen size
      const isSmallScreen = window.innerWidth <= 768;
      
      console.log('Is Mobile Device:', isMobileDevice);
      console.log('Has Touch:', hasTouch);
      console.log('Is Small Screen:', isSmallScreen);
      
      // Consider it mobile if it's a mobile device OR has touch support
      return isMobileDevice || hasTouch;
    };

    const mobile = isMobile();
    console.log('Final Mobile Detection:', mobile);

    return {
      timeAgo,
      decodeHtml,
      isMobile: mobile
    };
  },
};
</script>
<style scoped>
/* Featured Section Container */
.featured-section {
  margin: 60px 0;
  padding: 0 20px;
}

/* Section Header */
.section-header {
  margin-bottom: 40px;
}

.section-title {
  font-size: 32px;
  font-weight: 700;
  letter-spacing: -0.5px;
  color: #1a1a1a;
  margin: 0 0 12px 0;
}

.section-divider {
  width: 60px;
  height: 3px;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  border-radius: 2px;
}

/* Desktop View */
.desktop-view {
  display: block;
}

.featured-container {
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 32px;
}

/* Main Featured Post */
.main-featured {
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  background: #fff;
  container-type: inline-size;
}

.main-featured:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
}

.main-featured-link {
  display: block;
  text-decoration: none;
  color: inherit;
}

.main-image-wrapper {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 10;
  overflow: hidden;
  background: #f5f5f5;
  border-radius: 16px;
  container-type: inline-size;
}

.main-featured-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.main-featured:hover .main-featured-img {
  transform: scale(1.05);
}

.main-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.92) 0%,
    rgba(0, 0, 0, 0.7) 40%,
    rgba(0, 0, 0, 0.3) 70%,
    rgba(0, 0, 0, 0) 100%
  );
  padding: 48px 40px 40px;
  color: white;
}

.overlay-content {
  max-width: 100%;
}

.category-badge {
  display: inline-block;
  padding: 6px 14px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 16px;
  color: white;
}

.main-title {
  font-size: 42px;
  font-weight: 800;
  line-height: 1.15;
  margin: 0 0 16px 0;
  color: white;
  letter-spacing: -1px;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.main-excerpt {
  font-size: 16px;
  line-height: 1.6;
  margin: 0 0 16px 0;
  color: rgba(255, 255, 255, 0.9);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.main-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
}

.read-more-arrow {
  font-size: 20px;
  transition: transform 0.3s ease;
}

.main-featured:hover .read-more-arrow {
  transform: translateX(4px);
}

/* Secondary Featured Posts */
.secondary-featured {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  width: 100%;
}

.secondary-card {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border: 1px solid rgba(0, 0, 0, 0.04);
  min-height: 160px;
  display: flex;
  flex-direction: column;
}

.secondary-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(180deg, #667eea 0%, #764ba2 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.secondary-card:hover {
  transform: translateY(-4px) translateX(4px);
  box-shadow: 0 8px 32px rgba(102, 126, 234, 0.15);
  border-color: rgba(102, 126, 234, 0.2);
}

.secondary-card:hover::before {
  opacity: 1;
}

.secondary-link {
  display: block;
  text-decoration: none;
  color: inherit;
  height: 100%;
  padding: 18px 20px;
  position: relative;
}

.secondary-content {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
}

.secondary-category {
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #667eea;
  margin-bottom: 10px;
  display: inline-block;
  padding: 3px 8px;
  background: rgba(102, 126, 234, 0.1);
  border-radius: 10px;
  width: fit-content;
}

.secondary-title {
  font-size: 17px;
  font-weight: 700;
  line-height: 1.3;
  margin: 0 0 8px 0;
  color: #1a1a1a;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  letter-spacing: -0.2px;
}

.secondary-excerpt {
  font-size: 13px;
  line-height: 1.5;
  color: #666;
  margin: 0 0 10px 0;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.secondary-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
  padding-top: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.secondary-date {
  font-size: 12px;
  color: #909399;
  margin: 0;
  font-weight: 500;
}

.read-arrow {
  font-size: 18px;
  color: #667eea;
  transition: transform 0.3s ease;
  font-weight: 300;
}

.secondary-card:hover .read-arrow {
  transform: translateX(6px);
}

/* Mobile View */
.mobile-view {
  display: none;
}

@media (max-width: 768px) {
  .desktop-view {
    display: none;
  }

  .mobile-view {
    display: block;
  }

  .featured-section {
    margin: 40px 0;
    padding: 0 16px;
  }

  .section-title {
    font-size: 28px;
  }

  .mobile-featured {
    margin-bottom: 24px;
  }

  .mobile-main-link {
    display: block;
    text-decoration: none;
    color: inherit;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  }

  .mobile-image-wrapper {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 10;
    overflow: hidden;
  }

  .mobile-main-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .mobile-overlay {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0) 100%);
    padding: 24px 20px 20px;
    color: white;
  }

  .mobile-category {
    display: inline-block;
    padding: 4px 12px;
    background: rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(10px);
    border-radius: 16px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    margin-bottom: 12px;
  }

  .mobile-title {
    font-size: 24px;
    font-weight: 700;
    line-height: 1.3;
    margin: 0 0 8px 0;
    color: white;
  }

  .mobile-excerpt {
    font-size: 14px;
    line-height: 1.5;
    margin: 0 0 8px 0;
    color: rgba(255, 255, 255, 0.9);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .mobile-date {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.8);
    margin: 0;
  }

  .mobile-secondary {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .mobile-card {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    background: #fff;
  }

  .mobile-link {
    display: block;
    text-decoration: none;
    color: inherit;
    padding: 20px;
  }

  .mobile-card-content {
    display: flex;
    flex-direction: column;
  }

  .mobile-card-category {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    color: #667eea;
    margin-bottom: 6px;
  }

  .mobile-card-title {
    font-size: 18px;
    font-weight: 700;
    line-height: 1.4;
    margin: 0 0 8px 0;
    color: #1a1a1a;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .mobile-card-excerpt {
    font-size: 14px;
    line-height: 1.5;
    color: #666;
    margin: 0 0 12px 0;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .mobile-card-date {
    font-size: 12px;
    color: #909399;
    margin: 0;
    font-weight: 500;
  }
}

/* Responsive adjustments for tablets */
@media (max-width: 1024px) and (min-width: 769px) {
  .secondary-featured {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }
}

@media (max-width: 900px) and (min-width: 769px) {
  .secondary-featured {
    grid-template-columns: 1fr;
    gap: 16px;
  }
}
</style>
  